import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableRow from '@mui/material/TableRow'
import Typography from '@mui/material/Typography'

import StyledDetailsTable from '@components/StyledDetailsTable'
import { formatDate, formatDateTime } from '@util/date-util'

import { SidebarLayoutDrawer } from '../../../theme/components/SidebarLayoutDrawer'
import type { Activiteit } from '../types/Activiteit'

const generateJsxFromItems = ({ items }) => {
  return items.map((item) => (
    <TableRow key={`${item.title}-${item.text}`}>
      <TableCell>
        <b>{item.title}</b>
      </TableCell>
      <TableCell>{item.text}</TableCell>
    </TableRow>
  ))
}

const generateStatussen = ({ activiteit, statuses }) => {
  const items = [
    { title: 'Open', text: formatDateTime(activiteit.created.date) },
  ]

  if (statuses) {
    const sortedStatuses: any[] = [...statuses].sort(
      (a, b) =>
        Number(new Date(a.statusChangeDateTime)) -
        Number(new Date(b.statusChangeDateTime)),
    )

    for (const status of sortedStatuses) {
      const title = status.statusTypeText || status.statusType

      items.push({ title, text: formatDateTime(status.statusChangeDateTime) })
      if (status.statusChangeComment) {
        items.push({ title: '', text: status.statusChangeComment })
      }
    }
  }
  if (activiteit.completed) {
    items.push({
      title: 'Afgerond',
      text: formatDateTime(activiteit.completed.date),
    })
  }

  return generateJsxFromItems({ items })
}

const generateVestigingen = ({ vestigingen }) => {
  const items: any[] = []

  vestigingen.forEach((vestiging, index) =>
    items.push({ title: `Vestiging ${index + 1}`, text: vestiging.name }),
  )

  return generateJsxFromItems({ items })
}

const generateGeneralDetails = ({ data }) => {
  if (!data) return

  const {
    address,
    plannedstartdate,
    plannedcompleteddate,
    requestedstartdate,
    requestedenddate,
    psmorganisationname,
  } = data

  const items = [
    { title: 'Adres', text: address },
    { title: 'Wegbeheerder', text: psmorganisationname },
  ]

  if (plannedstartdate) {
    items.push({
      title: 'Datum start werk',
      text: formatDateTime(plannedstartdate),
    })
  }
  if (plannedcompleteddate) {
    items.push({
      title: 'Datum afgerond',
      text: formatDateTime(plannedcompleteddate),
    })
  }
  if (requestedstartdate) {
    items.push({
      title: 'Verzochte startdatum',
      text: formatDate(requestedstartdate),
    })
  }
  if (requestedenddate) {
    items.push({
      title: 'Verzochte einddatum',
      text: formatDate(requestedenddate),
    })
  }

  return generateJsxFromItems({ items })
}

interface Props {
  activiteit?: Activiteit
  onClose: () => void
  isExpanded: boolean
}

export const WowDrawer = ({ activiteit, onClose, isExpanded }: Props) => {
  const isGraafmelding = activiteit?.type.toLowerCase() === 'graafmelding'

  const data = activiteit?.data
  const typeObject =
    (isGraafmelding ? data?.graafmelding : data?.vergunning) || {}
  const id = isGraafmelding
    ? typeObject.roadwork?.id
    : typeObject.permit?.permitid
  const generalDetails = isGraafmelding
    ? typeObject.roadwork
    : typeObject.permit
  const statuses = typeObject.status

  // Single line assignment didn't work, because if the data.request.vestiging is undefined,
  // it will create an array with [undefined] (if you do it like [data?.request?.vestiging])
  const vestigingen: any[] = []

  if (isGraafmelding && data?.request?.vestigingen) {
    vestigingen.push(...data.request.vestigingen)
  } else if (!isGraafmelding && data?.request?.vestiging) {
    vestigingen.push(data.request.vestiging)
  }

  return activiteit ? (
    <SidebarLayoutDrawer
      open={isExpanded}
      onClose={onClose}
      title={[activiteit.type, id].filter(Boolean).join(' ')}
    >
      <Table>
        <TableBody>
          <TableRow>
            <TableCell colSpan={2}>
              <StyledDetailsTable>
                {generateGeneralDetails({ data: generalDetails })}
              </StyledDetailsTable>
            </TableCell>
          </TableRow>

          <TableRow>
            <TableCell colSpan={2}>
              <Typography>Vestigingen</Typography>
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell colSpan={2}>
              <StyledDetailsTable>
                {generateVestigingen({ vestigingen })}
              </StyledDetailsTable>
            </TableCell>
          </TableRow>

          <TableRow>
            <TableCell colSpan={2}>
              <Typography>Statusverloop</Typography>
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell colSpan={2}>
              <StyledDetailsTable>
                {generateStatussen({ activiteit, statuses })}
              </StyledDetailsTable>
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </SidebarLayoutDrawer>
  ) : (
    <></>
  )
}
